import React from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Icon,
  Card,
  CardBody,
  useColorModeValue,
  Badge,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { 
  FaCheck,
  FaRocket,
  FaEnvelope,
  FaCalendarAlt,
  FaUsers,
  FaChartLine,
  FaPhone,
  FaArrowRight,
  FaHome
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';

export default function LabSignupSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { plan, labData } = location.state || {};
  
  const bgGradient = useColorModeValue(
    'linear(to-br, green.50, blue.50)',
    'linear(to-br, gray.800, gray.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');

  const nextSteps = [
    {
      icon: FaEnvelope,
      title: "Check Your Email",
      description: "We've sent you a welcome email with your account details and next steps.",
      timeline: "Within 5 minutes"
    },
    {
      icon: FaCalendarAlt,
      title: "Onboarding Call",
      description: "Our team will contact you to schedule a personalized onboarding session.",
      timeline: "Within 24 hours"
    },
    {
      icon: FaRocket,
      title: "Profile Setup",
      description: "We'll help you create your professional laboratory profile and add your test offerings.",
      timeline: "1-2 business days"
    },
    {
      icon: FaUsers,
      title: "Go Live",
      description: "Your laboratory will be live on our platform and visible to patients and providers.",
      timeline: "2-3 business days"
    }
  ];

  const benefits = [
    "Professional laboratory profile with your branding",
    "Direct patient inquiries and booking requests",
    "Integration with our phlebotomist network",
    "Analytics dashboard to track performance",
    "SEO-optimized listing for local search",
    "Mobile-optimized presence"
  ];

  if (!plan || !labData) {
    return (
      <Box minHeight="100vh" bg={bgGradient}>
        <Container maxW="container.md" py={16}>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody p={8} textAlign="center">
              <VStack spacing={6}>
                <Heading size="lg" color={headingColor}>
                  Welcome to Our Laboratory Network!
                </Heading>
                <Text color={textColor}>
                  Thank you for joining our platform. We're excited to help grow your laboratory business.
                </Text>
                <Button
                  colorScheme="blue"
                  leftIcon={<Icon as={FaHome} />}
                  onClick={() => navigate('/lab-directory')}
                >
                  Go to Laboratory Directory
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" bg={bgGradient}>
      {/* Success Header */}
      <Box bg={cardBg} borderBottom="1px" borderColor={borderColor} py={12}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Box
              bg="green.100"
              p={6}
              borderRadius="full"
              border="4px"
              borderColor="green.200"
            >
              <Icon as={FaCheck} boxSize={12} color="green.600" />
            </Box>
            
            <VStack spacing={2}>
              <Badge colorScheme="green" size="lg" px={4} py={2} borderRadius="full">
                Payment Successful
              </Badge>
              <Heading size="2xl" color={headingColor}>
                Welcome to the Network!
              </Heading>
              <Text fontSize="xl" color={textColor} maxW="2xl">
                Congratulations, {labData.contactName}! {labData.labName} is now part of our 
                growing laboratory network with the {plan.name} plan.
              </Text>
            </VStack>
            
            <HStack spacing={8}>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="green.600">
                  14 Days
                </Text>
                <Text fontSize="sm" color={textColor}>Free Trial</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                  ${plan.price}
                </Text>
                <Text fontSize="sm" color={textColor}>Per {plan.period}</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                  24-48h
                </Text>
                <Text fontSize="sm" color={textColor}>Setup Time</Text>
              </VStack>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" py={12}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          {/* Next Steps */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody p={8}>
              <VStack spacing={6} align="stretch">
                <VStack spacing={2} align="start">
                  <Heading size="lg" color={headingColor}>
                    What Happens Next?
                  </Heading>
                  <Text color={textColor}>
                    Here's your step-by-step onboarding process to get your laboratory live on our platform.
                  </Text>
                </VStack>

                <VStack spacing={6} align="stretch">
                  {nextSteps.map((step, index) => (
                    <HStack key={index} align="start" spacing={4}>
                      <Box
                        bg={`${index === 0 ? 'green' : 'blue'}.100`}
                        p={3}
                        borderRadius="full"
                        border="2px"
                        borderColor={`${index === 0 ? 'green' : 'blue'}.200`}
                        minW="fit-content"
                      >
                        <Icon 
                          as={step.icon} 
                          boxSize={5} 
                          color={`${index === 0 ? 'green' : 'blue'}.600`} 
                        />
                      </Box>
                      <VStack align="start" spacing={1} flex="1">
                        <HStack justify="space-between" w="full">
                          <Text fontWeight="bold" color={headingColor}>
                            {step.title}
                          </Text>
                          <Badge colorScheme="gray" size="sm">
                            {step.timeline}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color={textColor}>
                          {step.description}
                        </Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>

                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      Need Help?
                    </Text>
                    <Text fontSize="sm">
                      Our support team is available to assist you throughout the onboarding process.
                    </Text>
                  </VStack>
                </Alert>
              </VStack>
            </CardBody>
          </Card>

          {/* Plan Benefits */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody p={8}>
              <VStack spacing={6} align="stretch">
                <VStack spacing={2} align="start">
                  <Heading size="lg" color={headingColor}>
                    Your {plan.name} Plan Includes
                  </Heading>
                  <Text color={textColor}>
                    Here's everything you get access to with your subscription.
                  </Text>
                </VStack>

                <List spacing={3}>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index}>
                      <HStack align="start">
                        <ListIcon as={FaCheck} color="green.500" mt={1} />
                        <Text fontSize="sm" color={textColor}>
                          {feature}
                        </Text>
                      </HStack>
                    </ListItem>
                  ))}
                </List>

                <VStack spacing={4}>
                  <Text fontSize="sm" color={textColor} textAlign="center">
                    You can upgrade, downgrade, or cancel your plan at any time from your dashboard.
                  </Text>
                  
                  <VStack spacing={3} w="full">
                    <Button
                      colorScheme="blue"
                      size="lg"
                      width="full"
                      rightIcon={<Icon as={FaArrowRight} />}
                      onClick={() => navigate('/lab-directory')}
                    >
                      Explore Laboratory Directory
                    </Button>
                    
                    <HStack spacing={4} w="full">
                      <Button
                        variant="outline"
                        colorScheme="green"
                        flex="1"
                        leftIcon={<Icon as={FaPhone} />}
                        onClick={() => window.location.href = 'tel:+1234567890'}
                      >
                        Call Support
                      </Button>
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        flex="1"
                        leftIcon={<Icon as={FaEnvelope} />}
                        onClick={() => window.location.href = 'mailto:support@company.com'}
                      >
                        Email Us
                      </Button>
                    </HStack>
                  </VStack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>
      </Container>

      {/* Footer CTA */}
      <Box bg={cardBg} borderTop="1px" borderColor={borderColor} py={12}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="lg" color={headingColor}>
              Ready to Start Growing Your Laboratory?
            </Heading>
            <Text color={textColor} maxW="2xl">
              While we set up your profile, explore our network and see how other laboratories 
              are successfully growing their business with our platform.
            </Text>
            <HStack spacing={4}>
              <Button
                colorScheme="blue"
                size="lg"
                leftIcon={<Icon as={FaUsers} />}
                onClick={() => navigate('/lab-directory')}
              >
                Browse Laboratory Network
              </Button>
              <Button
                variant="outline"
                colorScheme="purple"
                size="lg"
                leftIcon={<Icon as={FaChartLine} />}
                onClick={() => window.open('mailto:partnerships@company.com')}
              >
                Schedule Onboarding Call
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}

